// 일반적인 유틸리티 함수

import { FileInfo } from '@/common/_types/fetchData'
import { start } from 'repl'
import { pageableDefault } from '../_types/listType'

// milliseconds를 매개변수로 받아서 코드 실행을 지연시키는 함수
// 제출 버튼 비활성화 테스트용
// 1000 ms = 1 seconds
// 사용법: await delay(3000)
export const delay = async (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/*
 *
 *  입력 값 Validation 시작
 *
 */

/************************************************************************
  함수명  : isValidDateRange
  설  명  : 시작일과 종료일을 비교
  인  자  : stDateValue - 시작일 ("2024-09-10") 
          :endDateValue - 종료일 ("2024-09-11")
  사용법  : isValidDateRange(stDateValue, endDateValue)
  반환값  : 정상 true / 비정상 false (Boolean)
  작성일  : 2024.09.11
  작성자  : 김준태       
  수정일         수정자              수정내용
  ------        -------             -------------------
  2024.11.21    윤준현               한쪽 날짜에 '-'가 없이 들어오면 초기화되는 문제 해결
************************************************************************/
export const isValidDateRange = (
  stDateValue: string | number | undefined,
  endDateValue: string | number | undefined,
): boolean => {
  if (!stDateValue) return true
  if (!endDateValue) return true

  // 날짜 형식을 변환하는 함수
  const formatDate = (dateString: string): string => {
    if (!dateString.includes('-')) {
      return `${dateString.slice(0, 4)}-${dateString.slice(4, 6)}-${dateString.slice(6, 8)}`
    }
    return dateString
  }

  const formattedStDate = formatDate(String(stDateValue))
  const formattedEndDate = formatDate(String(endDateValue))

  const stDate = new Date(formattedStDate)
  const endDate = new Date(formattedEndDate)
  return stDate <= endDate
}

/*
 *
 *  입력 값 Validation 종료
 *
 */

/*
 *
 *  형식 변환 시작
 *
 */

/************************************************************************
  함수명  : toQueryString                                   
  설  명  : object를 queryString(string)으로 변환
  인  자  : obj - 쿼리스트링 ({page:1 , pageSize:10})
  반환값  : "?page=1&pageSize=10" (String)
  사용법  : toQueryString(obj)
  작성일  : 2024.09.11
  작성자  : 김준태       
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/
export function toQueryString(obj: object): string {
  let query: string = '?'
  // object key 배열로 저장
  let objKeys: Array<string> = Object.keys(obj)

  // 반복문으로 쿼리스트링 생성
  for (const [key, value] of Object.entries(obj)) {
    query += key.toString() + '=' + value
    // 마지막 값에는 "&" 제거
    if (objKeys[objKeys.length - 1] != key) {
      query += '&'
    }
  }
  return query
}

/************************************************************************
  함수명  : mergeAndGetQueryString                                   
  설  명  : paging param과 개별 검색어 param을 merge하여 queryString(string)으로 변환
  인  자  : pageParams - 페이징 정보 , serachParams - 검색조건 정보 
  반환값  : "?page=1&pageSize=10&searchValue=ABC"
  사용법  : mergeAndGetQueryString(pageParams, searchParams)
  작성일  : 2025.05.22
  작성자  : 이다빈       
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/
export function mergeAndGetQueryString(
  pageParams: pageableDefault,
  searchParams: object,
): string {
  let query: string = '?'

  const queryPageParams = {
    ...pageParams,
    page: pageParams.page,
  }

  for (const [key, value] of Object.entries(queryPageParams)) {
    if (value) {
      query += key.toString() + '=' + value + '&'
    }
  }
  for (const [key, value] of Object.entries(searchParams)) {
    if (value) {
      query += key.toString() + '=' + value + '&'
    }
  }

  return query
}

/************************************************************************
  함수명  : formatDate                                   
  설  명  : YYYYMMDD > YYYY-MM-DD 형태로 변환
  인  자  : date - 날짜 ("20240911")
  사용법  : formatDate(date)
  반환값  : "2024-09-11" (String)
  작성일  : 2024.09.11
  작성자  : 김준태       
  수정일         수정자              수정내용
  ------        -------             -------------------
  2024.12.26    윤준현               day가 없는 경우 맨 뒤에 하이픈 제거
************************************************************************/
export const formatDate = (date: string | undefined): string => {
  if (!date) return ''
  if (date && date.indexOf('-') >= 0) return date

  const year = date.substring(0, 4)
  const month = date.substring(4, 6)
  const day = date.substring(6)

  if (day) {
    return `${year}-${month}-${day}`
  } else {
    return `${year}-${month}`
  }
}

/************************************************************************
  함수명  : formatDateTime                                   
  설  명  : YYYYMMDDHHmmss > YYYY-MM-DD HH:mm:ss 형태로 변환
  인  자  : date - 날짜 ("20240911123059")
  사용법  : formatDateTime(date)
  반환값  : "2024-09-11 12:30:59" (String)
  작성일  : 2025.12.12
  작성자  : 이다빈       
************************************************************************/
export const formatDateTime = (date: string | undefined): string => {
  if (!date) return ''
  const ymd = date.substring(0, 8)
  const hms = date.substring(8, 14)
  return formatDate(ymd) + ' ' + formatHour(hms)
}

/************************************************************************
  함수명  : formatYear                                  
  설  명  : YYYYMM > YYYY 형태로 변환
  인  자  : date - 날짜 ("20240911")
  사용법  : formatYear(date)
  반환값  : "2024" (String)
  작성일  : 2025.05.26
  작성자  : 이혜진  
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/
export const formatYear = (date: string | undefined): string => {
  if (!date) return ''
  if (date && date.indexOf('-') >= 0) return date

  const year = date.substring(0, 4)

  return `${year}`
}

/************************************************************************
  함수명  : formatYm                                   
  설  명  : YYYYMM > YYYY-MM 형태로 변환
  인  자  : date - 날짜 ("20240911")
  사용법  : formatYm(date)
  반환값  : "2024-09" (String)
  작성일  : 2024.12.09
  작성자  : 김준태       
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/
export const formatYm = (date: string | undefined): string => {
  if (!date) return ''
  if (date && date.indexOf('-') >= 0) return date

  const year = date.substring(0, 4)
  const month = date.substring(4, 6)

  return `${year}-${month}`
}

/************************************************************************
  함수명  : formatBirth                                   
  설  명  : YYMMDD > YY-MM-DD 형태로 변환
  인  자  : date - 날짜 ("20250227")
  사용법  : formatBirth(date)
  반환값  : "73-04-09" (String)
  작성일  : 2025.02.26
  작성자  : 윤재효       
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/
export const formatBirth = (date: string | undefined): string => {
  if (!date) return ''
  if (date && date.indexOf('-') >= 0) return date

  const year = date.substring(0, 2)
  const month = date.substring(2, 4)
  const day = date.substring(4, 6)

  return `${year}-${month}-${day}`
}

/************************************************************************
  함수명  : formatHour                                   
  설  명  : HHmmss > HH:mm:ss / HH:mm:ss > HHmmss 형태로 변환
  인  자  : hour - 시간 ("235959", "23:59:59")
  사용법  : formatHour(hour)
  반환값  : "23:59:59", "235959" (String)
  작성일  : 2024.11.20
  작성자  : 윤준현       
  수정일         수정자              수정내용
  ------        -------             -------------------
  2025.01.24    윤준현               :이 들어간 경우면 제거해서 반환하도록 수정
************************************************************************/
export const formatHour = (hour: string | undefined): string => {
  if (!hour) return ''

  // 콜론이 있으면 제거
  if (hour.includes(':')) {
    return hour.replaceAll(':', '')
  }

  // 콜론 없으면 붙임
  if (hour.length == 6) {
    const HH = hour.slice(0, 2)
    const mm = hour.slice(2, 4)
    const ss = hour.slice(4, 6)
    return `${HH}:${mm}:${ss}`
  }

  // 형식이 잘못되었으면 원래 값 반환
  return hour
}

/************************************************************************
  함수명  : formatBizno                                   
  설  명  : 사업자등록번호에 '-'가 없는 경우 붙여서 반환
  인  자  : bizno - 사업자등록번호 ("1234567890")
  사용법  : formatBizno(bizno)
  반환값  : "123-45-67890" (String)
  작성일  : 2024.09.11
  작성자  : 김준태       
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/
export const formatBizno = (bizno: string | undefined): string => {
  if (!bizno) return ''
  if (bizno && bizno.indexOf('-') >= 0) return bizno

  const first = bizno.substring(0, 3)
  const second = bizno.substring(3, 5)
  const third = bizno.substring(5)

  return `${first}-${second}-${third}`
}

/************************************************************************
  함수명  : escapeHtml                                   
  설  명  : HTML 특수기호를 escape하는 함수
  인  자  : escape 처리할 HTML 엔터티 목록 text ("<")
  사용법  : escapeHtml(text)
  반환값  : "&lt;" (String)
  작성일  : 2025.02.14
  작성자  :        
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/
export const escapeHtml = (text: string | undefined): string => {
  if (text) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  } else {
    return ''
  }
}

/************************************************************************
  함수명  : unescapeHtml                                   
  설  명  : Escape 처리된 특수기호를 Unescape하는 함수
  인  자  : Unescape 처리할 HTML 엔터티 목록 text ("&lt;")
  사용법  : unescapeHtml(text)
  반환값  : "<" (String)
  작성일  : 2024.09.11
  작성자  :        
  수정일         수정자              수정내용
  ------        -------             -------------------
  2025.02.14    두윤기               문자열replace -> html변환
************************************************************************/
export const unescapeHtml = (text: string | undefined): string => {
  if (text) {
    const txt = document.createElement('textarea')
    txt.innerHTML = text
    return txt.value
  } else {
    return ''
  }
}

/************************************************************************
  함수명  : formatTelno                                   
  설  명  : 전화번호 하이픈 추가
  인  자  : telno 전화번호 01012345678
  사용법  : formatTelno(telno)
  반환값  : 010-1234-5678(string)
  작성일  : 2024.09.25
  작성자  : 김준태       
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/
export const formatTelno = (telno: string | undefined): string => {
  let result = ''
  if (!telno) return result
  if (telno.indexOf('-') >= 0) return telno
  if (telno.length < 8)
    result = telno
      .replace(/[^0-9]/g, '')
      .replace(/^(\d{0,3})(\d{0,3})(\d{0,4})$/g, '$1-$2-$3')
  switch (telno.length) {
    case 9:
      result = telno
        .replace(/[^0-9]/g, '')
        .replace(/^(\d{0,2})(\d{0,3})(\d{0,4})$/g, '$1-$2-$3')
      break
    case 10:
      if (telno.substring(0, 2) == '02') {
        result = telno
          .replace(/[^0-9]/g, '')
          .replace(/^(\d{0,2})(\d{0,4})(\d{0,4})$/g, '$1-$2-$3')
      } else {
        result = telno
          .replace(/[^0-9]/g, '')
          .replace(/^(\d{0,3})(\d{0,3})(\d{0,4})$/g, '$1-$2-$3')
      }
      break
    case 11:
      result = telno
        .replace(/[^0-9]/g, '')
        .replace(/^(\d{0,3})(\d{0,4})(\d{0,4})$/g, '$1-$2-$3')
      break
  }
  return result
}

/************************************************************************
  함수명  : formatNumber                                   
  설  명  : 123456 > 123,456 형태로 변환
  인  자  : num - 숫자 ("123456")
  사용법  : formatNumber(num)
  반환값  : "123,456" (String)
  작성일  : 2024.09.26
  작성자  : 김준태       
  수정일         수정자              수정내용
  ------        -------             -------------------
  2024.11.28    윤재효              formatNumber 0일때도 보이게 수정(조회수)
************************************************************************/
export const formatNumber = (num: string | number | undefined): string => {
  if (num === undefined || num === '') return '0'

  const currencyFormatter = new Intl.NumberFormat('ko-KR')

  return currencyFormatter.format(Number(num))
}

/************************************************************************
  camelCase를 snake_case로 변환하는 함수  
 ************************************************************************/
export const camelToSnakeCase = (str: string) => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

/************************************************************************
  함수명  : formatRcptNo                                   
  설  명  : 민원접수번호 하이픈 추가
  인  자  : rcptNo 민원접수번호 TKC00112345202400001
  사용법  : formatRcptNo(rcptNo)
  반환값  : TKC001-12345-2024-00001(string)
  작성일  : 2024.11.08
  작성자  : 김준태       
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/
export const formatRcptNo = (rcptNo: string | undefined): string => {
  let result = ''
  if (!rcptNo) return result
  if (rcptNo.indexOf('-') >= 0) return rcptNo
  result = rcptNo.replace(/^(.{6})(.{5})(.{4})(.{5})$/, '$1-$2-$3-$4')

  return result
}

/************************************************************************
  함수명  : formatCardNo                                   
  설  명  : 카드번호 하이픈 추가 및 마스킹  
  인  자  : cardNo: 카드번호 1234567890001234  
  사용법  : formatCardNo(cardNo)  
  반환값  : 1234-****-****-1234(string)  
  작성일  : 2024.12.02
  작성자  : 윤준현     
  수정일         수정자              수정내용
  ------        -------             -------------------
  2024.12.05    윤준현               중간 8자리 마스킹 처리 추가
************************************************************************/
export const formatCardNo = (cardNo: string): string => {
  // 빈 값이 들어올 경우 빈 문자열 리턴
  if (!cardNo) {
    return ''
  }

  // 입력된 카드번호에서 숫자만 추출
  const cleanedNumber = cardNo.replace(/\D/g, '')

  // 16자리가 아니면 원래 입력값 반환
  if (cleanedNumber.length !== 16) {
    return cardNo
  }

  // 중간 8자리를 별표로 마스킹
  const maskedNumber = cleanedNumber.replace(
    /(\d{4})(\d{8})(\d{4})/,
    '$1********$3',
  )

  // 4자리씩 끊어서 배열로 만들기
  const chunks = maskedNumber.match(/.{1,4}/g)

  // 배열을 '-'로 join하여 반환
  return chunks ? chunks.join('-') : cardNo
}

/************************************************************************
  함수명  : maskText                                   
  설  명  : 일반 텍스트 마스킹 함수
  인  자  : text: fsmsadmin
            option: masking option
  사용법  : maskText(text, {start : 2, end : 10, maskChar: '*'})  
  반환값  : fs********
  작성일  : 2025.01.06
  작성자  : 안성희     
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/

export const maskText = (
  text: string,
  option: {
    start: number
    end: number
    maskChar?: string
  },
): string => {
  if (!text) return ''

  const start = option.start <= 0 ? 0 : option.start
  const end = option.end >= text.length ? text.length : option.end
  const maskChar = !option.maskChar ? '*' : option.maskChar
  return (
    text.slice(0, start) +
    maskChar.repeat(end - start + 1) +
    text.slice(end + 1)
  )
}

/************************************************************************
  함수명  : formatVhclTy                                   
  설  명  : 차량 종류 문자열을 매핑하여 한글로 반환
  인  자  : vhclTy - 차량 종류 ("TRUCK", "TAX", "BUS")
  사용법  : formatVhclTy(vhclTy)
  반환값  : "화물차", "택시", "버스" (String)
  작성일  : 2024.12.16
  작성자  : 윤준현       
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/
export const formatVhclTy = (vhclTy: string): string => {
  const mapping: { [key: string]: string } = {
    TRUCK: '화물차',
    TAX: '택시',
    BUS: '버스',
  }

  return mapping[vhclTy] || '알 수 없음'
}

/*
 *
 *  형식 변환 종료
 *
 */

/*
 *
 *  파일 시작
 *
 */

// 인코딩된 URI를 디코딩하는 함수
// 파일 이름, URI에 한글이 포함되어 디코딩이 필요할 때 사용 가능
export const decodeUriString = (str: string): string => {
  try {
    return decodeURIComponent(str)
  } catch {
    return str
  }
}

// Bytes를 받아서 KB 혹은 MB로 변환하는 함수
const BYTES_IN_KB = 1024
const BYTES_IN_MB = BYTES_IN_KB * 1024
export const formatFileSize = (fileSize: number): string => {
  if (fileSize >= BYTES_IN_MB) {
    return `${(fileSize / BYTES_IN_MB).toFixed(2)} MB`
  } else if (fileSize >= BYTES_IN_KB) {
    return `${(fileSize / BYTES_IN_KB).toFixed(0)} KB`
  } else {
    return `${(fileSize / BYTES_IN_KB).toFixed(2)} KB` // 1KB 미만이라면 소수점 2자리까지 KB 표시
  }
}

// 파일 객체 배열과 Set<string>을 매개변수로 받음
export const filterDuplicateFiles = (
  files: File[],
  seenFiles: Set<string>, // React.MutableRefObject를 사용하지 않고 Set<string>을 직접 사용
): File[] => {
  if (!files) return [] //file이 undfined or null일 경우 빈 배열 반환 - 윤재효
  // 파일의 고유성을 파일 이름과 크기로 식별
  return files.filter((file: File): boolean => {
    const identifier: string = `${file.name}-${file.size}`

    // 파일이 이미 존재하는지 확인
    if (seenFiles.has(identifier)) {
      return false // 중복된 파일
    } else {
      seenFiles.add(identifier) // 파일 추가
      return true // 고유한 파일
    }
  })
}

export const compareFiles = (
  newFiles: File[], // 새 파일 배열
  existingFiles: FileInfo[], // 기존 파일 배열
  deletedFileList: string[] = [], // 삭제된 파일의 ID 리스트
): File[] => {
  // 삭제된 파일을 제외한 기존 파일의 식별자를 Set으로 변환
  const existingFileIdentifiers = new Set(
    existingFiles
      .filter((file) => {
        // deletedFileList에 포함되지 않은 파일만 선택
        const isDeleted = deletedFileList.includes(file.fileTsid)
        return !isDeleted
      })
      .map((file) => `${file.fileNm}-${file.fileSize}`),
  )

  // 새 파일 배열에서 기존 파일과 중복된 파일을 필터링
  const uniqueFiles = newFiles.filter((file: File) => {
    const identifier: string = `${file.name}-${file.size}`
    const isUnique = !existingFileIdentifiers.has(identifier)
    return isUnique
  })

  return uniqueFiles
}

/*
 *
 *  파일 종료
 *
 */

/*
 *
 *  기타 시작
 *
 */

/************************************************************************
  함수명  : pagingNumber                                   
  설  명  : 총 검색 수와 페이지 사이즈로 총 페이지 수 계산
  인  자  : total - 총 검색 수 , size - 페이지 사이즈
  반환값  : 1 (number) 소숫점 제거
  사용법  : pagingNumber(total, size)
  작성일  : 2024.09.24
  작성자  : 김준태       
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/
export function pagingNumber(total: number, size: number): number {
  let paging: number =
    size > total ? 1 : total % size > 0 ? total / size + 1 : total / size
  return ~~paging
}

const today = new Date()
const kstDate = new Date(today.getTime() + 9 * 60 * 60 * 1000)
/************************************************************************
  변수명  : formatNumber  
  설  명  : 한국 시간대의 오늘 날짜를 출력하기 위한 변수  
  작성일  : 2024.10.15  
  작성자  : 윤준현  
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/
export const formattedDate = kstDate.toISOString().split('T')[0]

/************************************************************************
  변수명  : enumToArray  
  설  명  : enum 을 열거형으로 변환 
  작성일  : 2025.03.05  
  작성자  : 안성희  
  수정일         수정자              수정내용
  ------        -------             -------------------
************************************************************************/
export function enumToArray<T>(enumObject: { [key: string]: T }): T[] {
  return Object.keys(enumObject)
    .filter((key) => isNaN(Number(key)))
    .map((key) => enumObject[key] as unknown as T)
}

/*
 *
 *  기타 종료
 *
 */
